import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Item } from "./Item";
import { device, icon } from "./settings";
import { useTracks } from "../../context/TracksContext";
import { auth, db } from "../../firebase-config";
import { doc, getDoc } from "firebase/firestore";

// Download the Framer Beta: https://www.framer.com/beta/
// Framer Beta API documentation: https://www.framer.com/api/

// Moods from MoodSelector with emojis and background images
const MOODS = [
  { label: "Party", emoji: "🎉" },
  { label: "Chill", emoji: "😌" },
  { label: "Energetic", emoji: "🔥" },
  { label: "Romantic", emoji: "❤️" },
];

// Mood background images mapping
const MOOD_IMAGES = {
  "Party": "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fparty-mood.png?alt=media&token=890ef763-5261-4a75-b924-0438109a48ed",
  "Chill": "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fchill-mood.png?alt=media&token=034a14ff-1e13-4804-aa01-c5875f07d22b",
  "Energetic": "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fenergetic-mood.png?alt=media&token=083fa7f1-b111-4efe-8ea1-c0e993a1aac5",
  "Romantic": "https://firebasestorage.googleapis.com/v0/b/chatbout001.appspot.com/o/images%2Fromantic-mood.png?alt=media&token=50ae15b8-054e-4908-ac29-c8d6231af77b"
};

// Utility function to group tracks by genre and extract unique genres
const extractUniqueGenres = (tracks) => {
  if (!tracks || tracks.length === 0) return [];

  const genreSet = new Set();
  tracks.forEach((track) => {
    if (track.genrekey) {
      genreSet.add(track.genrekey);
    }
  });

  return Array.from(genreSet).sort();
};

// Utility function to create a seeded random number generator
const seededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};

// Utility function to shuffle array with a seed for consistent results
const shuffleArrayWithSeed = (array, seed) => {
  const shuffled = [...array];
  const random = seededRandom(seed);

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Generate a stable seed once for the session
const SESSION_SEED = Date.now();

export function AppleWatchDock({ onSelectionsChange, countdown, cachedSelections }) {
  // Get tracks from context
  const { tracks } = useTracks();

  // Loading state for Firebase data
  const [isLoading, setIsLoading] = useState(!cachedSelections);

  // Selection state - max 2 moods and 3 genres
  // Initialize from cached selections if available
  const [selectedMoods, setSelectedMoods] = useState(() => {
    if (cachedSelections?.moods) {
      return cachedSelections.moods.filter(mood => MOODS.some(m => m.label === mood));
    }
    return [];
  });
  const [selectedGenres, setSelectedGenres] = useState(() => {
    return cachedSelections?.genres || [];
  });

  // Load previous selections from Firestore on mount (only if not cached)
  useEffect(() => {
    // Skip if we already have cached selections
    if (cachedSelections) {
      setIsLoading(false);
      return;
    }

    const loadPreviousSelections = async () => {
      if (!auth.currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Load saved moods and genres, filtering out any that no longer exist
          if (userData.selectedMoods) {
            const validMoods = userData.selectedMoods.filter(mood =>
              MOODS.some(m => m.label === mood)
            );
            setSelectedMoods(validMoods);
          }

          if (userData.selectedGenres) {
            setSelectedGenres(userData.selectedGenres);
          }
        }
      } catch (error) {
        console.error("Error loading previous selections:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreviousSelections();
  }, [cachedSelections]);

  // Notify parent component when selections change
  useEffect(() => {
    if (onSelectionsChange) {
      onSelectionsChange({ moods: selectedMoods, genres: selectedGenres });
    }
  }, [selectedMoods, selectedGenres, onSelectionsChange]);

  // Handle item click to toggle selection
  const handleItemClick = (label, isMood) => {
    if (isMood) {
      // Handle mood selection (max 2)
      if (selectedMoods.includes(label)) {
        // Deselect
        setSelectedMoods(prev => prev.filter(m => m !== label));
      } else if (selectedMoods.length < 2) {
        // Select
        setSelectedMoods(prev => [...prev, label]);
      }
    } else {
      // Handle genre selection (max 3)
      if (selectedGenres.includes(label)) {
        // Deselect
        setSelectedGenres(prev => prev.filter(g => g !== label));
      } else if (selectedGenres.length < 3) {
        // Select
        setSelectedGenres(prev => [...prev, label]);
      }
    }
  };

  // Combine unique genres from tracks with moods and shuffle them with a stable seed
  // Filter out selected items so they don't appear in the main grid
  const genresAndMoods = useMemo(() => {
    const genres = extractUniqueGenres(tracks);
    // Convert genres to objects for consistent handling
    const genreObjects = genres.map(genre => ({ label: genre, emoji: null }));
    const combined = [...MOODS, ...genreObjects];

    // Filter out items that are already selected
    const allSelected = [...selectedMoods, ...selectedGenres];
    const filtered = combined.filter(item => !allSelected.includes(item.label));

    // Use a deterministic seed based on the combined items to ensure consistent shuffle
    const seed = SESSION_SEED + filtered.length;
    return shuffleArrayWithSeed(filtered, seed);
  }, [tracks, selectedMoods, selectedGenres]);

  // Calculate dynamic grid dimensions
  const numItems = genresAndMoods.length;
  // Use 2 columns for 4 or fewer items, otherwise 6 columns
  const numCols = numItems <= 4 ? 2 : 6;
  const numRows = Math.ceil(numItems / numCols);

  // Calculate dynamic icon size - double the size if 4 or fewer items
  const iconSize = numItems <= 4 ? icon.size * 2 : icon.size;
  const iconMargin = numItems <= 4 ? icon.margin * 2 : icon.margin;

  // Create dynamic grid structure
  const grid = useMemo(() => {
    const gridArray = [];
    for (let row = 0; row < numRows; row++) {
      const rowArray = [];
      for (let col = 0; col < numCols; col++) {
        const index = row * numCols + col;
        if (index < numItems) {
          rowArray.push(col);
        }
      }
      if (rowArray.length > 0) {
        gridArray.push(rowArray);
      }
    }
    return gridArray;
  }, [numItems, numCols, numRows]);

  // Calculate drag constraints based on grid size and dynamic icon size
  const dragConstraints = useMemo(() => {
    return {
      left: -(numRows * (iconSize + iconMargin)),
      right: 50,
      top: -(numRows * iconSize),
      bottom: 50
    };
  }, [numRows, iconSize, iconMargin]);

  // We manually create x/y motion values for the draggable plane as it allows us to pass these to
  // icon children, which can then listen to when they change and respond.
  // Start with a slight offset to show it's already "scrolled" and animated
  const x = useMotionValue(-30);
  const y = useMotionValue(-20);

  // Add subtle nudge animation on mount to show interactivity
  useEffect(() => {
    // Animate to slightly different position for a "settling" effect
    animate(x, -40, { duration: 0.6, ease: "easeOut" });
    animate(y, -30, { duration: 0.6, ease: "easeOut" });
  }, [x, y]);

  // Combine all selected items for bottom display
  const allSelectedItems = useMemo(() => {
    const moodItems = selectedMoods.map(label => ({
      label,
      isMood: true,
      moodImage: MOOD_IMAGES[label]
    }));
    const genreItems = selectedGenres.map(label => ({
      label,
      isMood: false,
      moodImage: null
    }));
    return [...moodItems, ...genreItems];
  }, [selectedMoods, selectedGenres]);

  // Show loading skeleton while data is being fetched
  if (isLoading) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            display: "flex",
            gap: "12px"
          }}>
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.1)",
                  animation: "pulse 1.5s ease-in-out infinite",
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
          <div style={{
            color: "rgba(255, 255, 255, 0.5)",
            fontSize: "0.9rem"
          }}>
            Loading...
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.6; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Main draggable grid - reduced height to make room for bottom list */}
      <div className="device" style={{ ...device, height: device.height - 100 }}>
        <motion.div
          drag
          dragConstraints={dragConstraints}
          style={{
            // Likewise these draggable plane dimensions could be calculated, but 1000x1000 is arbitrary and big
            width: 1000,
            height: 1000,
            x,
            y,
            background: "transparent"
          }}
        >
          {grid.map((rows, rowIndex) =>
            rows.map(colIndex => {
              const index = rowIndex * numCols + colIndex;
              const item = genresAndMoods[index];
              const isMood = item.emoji !== null;
              const isSelected = false; // Never selected in main grid anymore
              const moodImage = isMood ? MOOD_IMAGES[item.label] : null;

              return (
                <Item
                  key={`${rowIndex}-${colIndex}`}
                  row={rowIndex}
                  col={colIndex}
                  planeX={x}
                  planeY={y}
                  label={item.label}
                  isMood={isMood}
                  moodImage={moodImage}
                  selected={isSelected}
                  onClick={() => handleItemClick(item.label, isMood)}
                  iconSize={iconSize}
                  iconMargin={iconMargin}
                />
              );
            })
          )}
        </motion.div>
      </div>

      {/* Bottom selection container with frosted glass effect */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "100px",
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderTop: "1px solid rgba(255, 255, 255, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        padding: "0 20px",
        overflow: "hidden"
      }}>
        {allSelectedItems.length === 0 ? (
          <div style={{
            color: "rgba(255, 255, 255, 0.5)",
            fontSize: "0.9rem",
            textAlign: "center"
          }}>
            Select up to 2 moods and 3 genres
          </div>
        ) : (
          allSelectedItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleItemClick(item.label, item.isMood)}
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: item.moodImage ? `url(${item.moodImage})` : `linear-gradient(135deg, hsl(${index * 60}, 95%, 65%), hsl(${index * 60}, 95%, 40%))`,
                backgroundSize: item.moodImage ? "150%" : "auto",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                border: "3px solid #FFD700",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: "#ffffff",
                textShadow: "0 0 6px rgba(0, 0, 0, 0.9), 1px 1px 2px rgba(0, 0, 0, 0.8)",
                cursor: "pointer",
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "4px",
                boxSizing: "border-box"
              }}
            >
              {item.label}
            </motion.div>
          ))
        )}

        {/* Countdown timer display */}
        {countdown !== null && countdown >= 0 && (
          <div style={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            fontSize: "0.9rem",
            fontWeight: "bold",
            color: "rgba(255, 255, 255, 0.7)",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)"
          }}>
            {countdown}
          </div>
        )}
      </div>
    </div>
  );
}
